/**
 * Returns true if email verification is required for the given auth user.
 *
 * Supabase's /auth/v1/signup response OMITS `email_confirmed_at` entirely for
 * unconfirmed users (it is NOT returned as `null`). A strict `=== null` check
 * therefore yields false on unconfirmed users, silently sending the signup flow
 * down the auto-confirmed branch. We use a truthy check so both missing and
 * null are treated as "not confirmed → verification required".
 */
export const detectEmailVerificationEnabled = (user: unknown): boolean => {
  const confirmedAt = (user as { email_confirmed_at?: string | null })?.email_confirmed_at;
  return !confirmedAt;
};

/**
 * Returns true only for Supabase's specific "Email not confirmed" error.
 *
 * Deliberately does NOT match "Invalid login credentials" — that is Supabase's
 * intentionally ambiguous error for wrong-password OR nonexistent-account.
 * Treating it as "unconfirmed" and showing a "resend verification" CTA would
 * let an attacker probe whether an email is registered by watching which UI
 * appears after failed login (account enumeration vulnerability).
 */
export const isUnconfirmedUser = (error: unknown): boolean => {
  const message = (error as { message?: string })?.message;
  return typeof message === "string" && message.includes("Email not confirmed");
};

/**
 * Build the verification callback URL that Supabase Auth redirects to after
 * the user clicks a confirmation / recovery / magic-link email.
 *
 * The callback page (`/auth/confirm`) picks up the active session and forwards
 * the user to `next`. Default `/` lands on the authenticated dashboard.
 */
export const getVerificationRedirectUrl = (next = "/"): string => {
  const params = new URLSearchParams({ next });
  return `${window.location.origin}/auth/confirm?${params.toString()}`;
};

/**
 * Where the OAuth round-trip state travels: a query param on `redirectTo`, which Supabase
 * carries through the provider round trip untouched (the grant arrives in the fragment).
 */
export const OAUTH_STATE_PARAM = "oauth_state";

/** Where the value it must match is kept — see beginOAuthRoundTrip. */
const OAUTH_STATE_KEY = "franklin-oauth-state";

/**
 * Mint the value that ties an OAuth redirect back to the sign-in THIS browser started, and
 * stash it for the callback to check.
 *
 * The implicit flow carries no `state` and no PKCE verifier, so nothing in the URL that comes
 * back from Google is bound to the click that left. Without this the callback believes any
 * `#access_token=…` it is handed — including a VALID one, minted by an attacker on their own
 * Google account and delivered by a link to `/auth/callback#access_token=<attacker>…`. The
 * victim would be silently signed in as the attacker, and every document they upload next
 * would be filed in the attacker's org. That is login CSRF / session fixation, and the state
 * is what closes it for this flow.
 *
 * sessionStorage, not localStorage: the round trip belongs to the one tab that started it, and
 * the value should die with that tab. Null when storage refuses (Safari private mode, blocked
 * cookies) — a round trip that cannot be verified must not be started, and nothing is lost by
 * refusing it: the same block stops supabase-js persisting the session it would produce.
 */
/**
 * A random value to bind the round trip with.
 *
 * `crypto.randomUUID` is secure-context-only and absent before Safari 15.4, so on a LAN-IP dev
 * or preview origin (`http://192.168.x.x:8080`) it throws — and since the throw is caught as
 * "storage refused", Google sign-in would be dead for that browser with only the generic toast
 * to go on. `crypto.getRandomValues` carries neither restriction and is just as unguessable.
 */
const randomState = (): string =>
  typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) =>
        byte.toString(16).padStart(2, "0")
      ).join("");

export const beginOAuthRoundTrip = (): string | null => {
  try {
    const state = randomState();
    sessionStorage.setItem(OAUTH_STATE_KEY, state);
    return state;
  } catch {
    return null;
  }
};

/**
 * Does the state on the callback URL match the one this tab stashed? False when nothing was
 * stashed or storage refuses — a grant this browser cannot vouch for is not one to accept.
 */
export const matchesOAuthRoundTrip = (state: string | null): boolean => {
  try {
    const expected = sessionStorage.getItem(OAUTH_STATE_KEY);
    return Boolean(expected) && expected === state;
  } catch {
    return false;
  }
};

/**
 * Drop the stashed state once the round trip it belongs to has landed.
 *
 * Deliberately not folded into matchesOAuthRoundTrip: the callback effect can run more than
 * once for a single page load, and a check that consumed on read would fail the second run —
 * signing out a user whose sign-in actually succeeded.
 */
export const endOAuthRoundTrip = (): void => {
  try {
    sessionStorage.removeItem(OAUTH_STATE_KEY);
  } catch {
    // Storage refused; there is nothing stashed to clean up.
  }
};

/**
 * The pages that are supposed to be handed a grant in the URL, and that read one: the OAuth
 * landing (/auth/callback, which then checks the round-trip state), the email confirmation
 * landing (/auth/confirm) and the recovery landing (/reset-password, which hand-parses
 * `#access_token` + `type=recovery`). Matched exactly — anything else strips, which is the
 * safe direction.
 */
const GRANT_PATHS = ["/auth/callback", "/auth/confirm", "/reset-password"];

/** What gotrue reads a session out of, in the fragment or the query. */
const GRANT_PARAMS = ["access_token", "refresh_token"];

/**
 * The same URL with a grant nobody asked for removed, or null when there is nothing to strip.
 *
 * The round-trip state above only guards /auth/callback, but gotrue does not care what page it
 * is on: `_initialize()` runs `parseParametersFromURL(window.location.href)` — hash params with
 * the query merged over them — and `_isImplicitGrantCallback` matches on `access_token` alone
 * (auth-js 2.70). So `https://app/buildings#access_token=<attacker's own valid token>&…` is
 * adopted and persisted while the client initialises, on a route with no callback to refuse it:
 * session fixation, one path segment away from the control that exists to stop it. Removing the
 * grant before `createClient()` reads it is the containment for every other route while the app
 * stays on the implicit flow; `flowType: 'pkce'` is what removes the shape of the attack.
 *
 * Called with `window.location.href`; the result goes through `history.replaceState`.
 */
export const stripUnexpectedGrant = (href: string): string | null => {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }

  if (GRANT_PATHS.includes(url.pathname)) return null;

  const hashParams = new URLSearchParams(url.hash.slice(1));
  if (!GRANT_PARAMS.some((key) => hashParams.has(key) || url.searchParams.has(key))) return null;

  // The whole fragment goes: it is a grant's fragment, not a link to an anchor.
  url.hash = "";
  for (const key of GRANT_PARAMS) url.searchParams.delete(key);
  return url.toString();
};

/**
 * Reduce an untrusted `?next=` to a path on our own origin, or fall back.
 *
 * The auth callbacks read `next` straight out of a link, so it is fully
 * attacker-supplied. A protocol-relative `//evil.com` makes react-router's
 * `history.replaceState` throw a cross-origin SecurityError, and the router
 * then falls back to `window.location.assign(url)` — an open redirect starting
 * from a trusted app URL.
 *
 * Resolving against our own origin and re-checking it is what makes this safe
 * for the awkward cases too: the URL parser folds `\` into `/` (so `/\evil.com`
 * resolves off-origin and is rejected) and gives `javascript:` an opaque origin.
 */
export const safeNextPath = (next: string | null | undefined, fallback: string): string => {
  if (!next) return fallback;

  try {
    const url = new URL(next, window.location.origin);
    return url.origin === window.location.origin
      ? `${url.pathname}${url.search}${url.hash}`
      : fallback;
  } catch {
    return fallback;
  }
};
